import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const Form_14 = () => {
    const navigate = useNavigate();

    const commonFields = [
        { label: 'Date', name: 'date', type: 'date', readOnly: true },
        { label: 'Shift', name: 'shift', type: 'text', readOnly: true },
    ];

    const lineFields = [
        { label: 'Time', name: 'time', type: 'text', readOnly: true },
        { label: 'Make', name: 'make' },
        { label: 'A Glue', name: 'aGlue', type: 'number' },
        { label: 'B Glue', name: 'bGlue', type: 'number' },
        { label: 'Total Glue', name: 'totalGlue', type: 'number', readOnly: true },
        { label: 'Ratio', name: 'ratio', type: 'number', readOnly: true },
        { label: 'Remark', name: 'remark', type: 'textarea' },
    ];

    const getCurrentTime = () => {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const [lineOptions, setLineOptions] = useState([]);
    const [formData, setFormData] = useState({
        date: '',
        shift: '',
        lines: {},
        selectedLine: '',
    });

    const [isLoading, setIsLoading] = useState(false);

    // Initialize line options from metadata
    useEffect(() => {
        const metadata = sessionStorage.getItem('metadata');
        if (metadata) {
            try {
                const parsedMetadata = JSON.parse(metadata);
                const lines = parsedMetadata.lines?.map(line => line.Lines) || [];
                setLineOptions(lines);

                setFormData(prevData => {
                    const existingLines = Object.keys(prevData.lines);
                    const newLines = lines.reduce((acc, line) => {
                        if (!existingLines.includes(line)) {
                            acc[line] = lineFields.reduce((fieldAcc, field) => ({
                                ...fieldAcc,
                                [field.name]: field.name === 'time' ? getCurrentTime() : ''
                            }), {});
                        }
                        return acc;
                    }, {});

                    return {
                        ...prevData,
                        lines: {
                            ...prevData.lines,
                            ...newLines
                        },
                        selectedLine: prevData.selectedLine || (lines[0] || '')
                    };
                });
            } catch (error) {
                console.error('Error parsing metadata:', error);
            }
        }
    }, []);

    // Load inspection date and shift
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        if (inspectionDate && inspectionShift) {
            setFormData((prevData) => ({
                ...prevData,
                date: inspectionDate,
                shift: inspectionShift,
            }));
        }
    }, []);

    // Load prefilled data, prioritizing inspectionData
    useEffect(() => {
        if (lineOptions.length === 0) return;

        let updatedData = { ...formData };

        // Try to load from inspectionData
        const inspectionData = sessionStorage.getItem('inspectionData');
        const savedFormData = sessionStorage.getItem('JunctionBoxPottingF14formData');

        const parsedInspectionData = inspectionData ? JSON.parse(inspectionData) : {};
        const parsedSavedData = savedFormData ? JSON.parse(savedFormData) : {};
        const jbData = parsedInspectionData?.JunctionBoxPottingMaterial || {};

        updatedData.date = jbData.date || parsedSavedData.date || updatedData.date;
        updatedData.shift = jbData.shift || parsedSavedData.shift || updatedData.shift;

        // Build lines object with fallback
        const filledLines = lineOptions.reduce((acc, line) => {
            const fromInspection = jbData.lines?.[line] || {};
            const fromSaved = parsedSavedData.lines?.[line] || {};

            acc[line] = {
                time: fromInspection.time || fromSaved.time || getCurrentTime(),
                make: fromInspection.make || fromSaved.make || '',
                aGlue: fromInspection.aGlue || fromSaved.aGlue || '',
                bGlue: fromInspection.bGlue || fromSaved.bGlue || '',
                totalGlue: fromInspection.totalGlue || fromSaved.totalGlue || '',
                ratio: fromInspection.ratio || fromSaved.ratio || '',
                remark: fromInspection.remark || fromSaved.remark || '',
            };

            return acc;
        }, {});

        updatedData.lines = filledLines;
        updatedData.selectedLine = parsedSavedData.selectedLine || updatedData.selectedLine || lineOptions[0];

        // Recalculate glue for selected line
        if (updatedData.selectedLine && updatedData.lines[updatedData.selectedLine]) {
            updatedData = calculateGlueValues(updatedData, updatedData.selectedLine);
        }

        setFormData(updatedData);
    }, [lineOptions]);


    useEffect(() => {
        sessionStorage.setItem('JunctionBoxPottingF14formData', JSON.stringify(formData));
    }, [formData]);

    // Update time when switching lines
    useEffect(() => {
        if (formData.selectedLine && formData.lines[formData.selectedLine]?.time === '') {
            setFormData(prevData => ({
                ...prevData,
                lines: {
                    ...prevData.lines,
                    [prevData.selectedLine]: {
                        ...prevData.lines[prevData.selectedLine],
                        time: getCurrentTime(),
                    },
                },
            }));
        }
    }, [formData.selectedLine]);

    // Function to calculate Total Glue and Ratio
    const calculateGlueValues = (data, selectedLine) => {
        if (!data.lines[selectedLine]) return data;

        const aGlue = parseFloat(data.lines[selectedLine].aGlue) || 0;
        const bGlue = parseFloat(data.lines[selectedLine].bGlue) || 0;

        const totalGlue = aGlue + bGlue;
        const ratio = bGlue !== 0 ? (aGlue / bGlue).toFixed(2) : '';

        return {
            ...data,
            lines: {
                ...data.lines,
                [selectedLine]: {
                    ...data.lines[selectedLine],
                    totalGlue: totalGlue.toString(),
                    ratio: ratio.toString(),
                },
            },
        };
    };

    const handleCommonChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({ ...prevData, [name]: value }));
    };

    const handleLineChange = (e) => {
        setFormData((prevData) => ({ ...prevData, selectedLine: e.target.value }));
    };

    const handleLineFieldChange = (e) => {
        const { name, value } = e.target;

        setFormData((prevData) => {
            const updatedLine = {
                ...prevData.lines[prevData.selectedLine],
                [name]: value,
                time: getCurrentTime(), // Update time on any change
            };

            let updatedData = {
                ...prevData,
                lines: {
                    ...prevData.lines,
                    [prevData.selectedLine]: updatedLine,
                },
            };

            // Recalculate glue values if aGlue or bGlue is changed
            if (name === 'aGlue' || name === 'bGlue') {
                updatedData = calculateGlueValues(updatedData, prevData.selectedLine);
            }

            return updatedData;
        });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: formData.date,
                inspectionShift: formData.shift,
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                JunctionBoxPottingMaterial: {
                    lines: formData.lines,
                },
            };

            await submitFormData(payload);
            alert('Form submitted successfully!');

            // Optionally, clear all lines or just selected line
            // setFormData(prevData => {
            //     const clearedLines = Object.fromEntries(
            //         Object.entries(prevData.lines).map(([line, _]) => [
            //             line,
            //             lineFields.reduce((acc, field) => ({
            //                 ...acc,
            //                 [field.name]: field.name === 'time' ? getCurrentTime() : '',
            //             }), {})
            //         ])
            //     );

            //     return {
            //         ...prevData,
            //         lines: clearedLines,
            //     };
            // });
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-lg space-y-4">
            <h2 className="text-2xl font-bold text-center mb-4">Junction Box Potting Material Ratio Verification</h2>
            <p className="text-center font-semibold mb-4">Frequency: Once per shift</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {commonFields.map(({ label, name, type, readOnly }) => (
                    <div key={name}>
                        <label className="block font-semibold mb-1">{label}</label>
                        <input
                            type={type}
                            name={name}
                            value={formData[name] || ''}
                            onChange={handleCommonChange}
                            readOnly={readOnly}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-100' : ''}`}
                        />
                    </div>
                ))}
                <div>
                    <label className="block font-semibold mb-1">Line</label>
                    <select
                        name="selectedLine"
                        value={formData.selectedLine || ''}
                        onChange={handleLineChange}
                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                    >
                        {lineOptions.map((line) => (
                            <option key={line} value={line}>
                                {line}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <h3 className="text-xl font-semibold">JB Potting ratio verification</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {lineFields.slice(0, 6).map(({ label, name, type, readOnly }) => (
                    <div key={name}>
                        <label className="block font-semibold mb-1">{label}</label>
                        <input
                            type={type || 'text'}
                            name={name}
                            value={formData.lines[formData.selectedLine]?.[name] || ''}
                            onChange={(e) => !readOnly && handleLineFieldChange(e)}
                            readOnly={readOnly}
                            className={`w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-100' : ''}`}
                        />
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {lineFields.slice(6).map(({ label, name, type }) => (
                    <div key={name}>
                        <label className="block font-semibold mb-1">{label}</label>
                        <textarea
                            name={name}
                            value={formData.lines[formData.selectedLine]?.[name] || ''}
                            onChange={handleLineFieldChange}
                            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                ))}
            </div>

            <div className="flex justify-between mt-4">
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form13')}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2 bg-[#424752] text-white rounded hover:bg-gray-500 disabled:bg-gray-400"
                >
                    {isLoading ? 'Submitting...' : 'Submit'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate('/inspections/section2/form15')}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default Form_14;